import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useMemo, useRef, useState } from "react";

import getAppConfig from "@liberty-fund/config";
import { useAppDispatch } from "@liberty-fund/data-context/hooks";
import {
  login,
  logout,
} from "@liberty-fund/data-context/reducers/account-reducer";
import type { User } from "@liberty-fund/data-context/types";
import Logger from "@liberty-fund/logger";
import { decodeJwtPayload, getAccessToken } from "@liberty-fund/utils/auth";

/**
 * Mirrors the Auth0 session into Redux, replacing the fetch to
 * `/api/auth/identity` that decoded the ALB's `x-amzn-oidc-data` header.
 *
 * Roles come from a namespaced custom claim on the ID token. Permissions are
 * read from the ID token when the tenant projects them there, falling back to
 * the access token — Auth0's RBAC "Add Permissions in the Access Token" setting
 * puts them only on the access token, and which of the two applies is a tenant
 * configuration detail this app shouldn't depend on.
 */
const useAuthListener = () => {
  const { user, isAuthenticated, isLoading, error } = useAuth0();
  const dispatch = useAppDispatch();
  const [permissions, setPermissions] = useState<string[] | undefined>();

  const config = getAppConfig();
  const { rolesKey, permissionsKey } = config.auth0.customClaims;

  const roles = useMemo(
    () => (user?.[rolesKey] as string[] | undefined) ?? [],
    [rolesKey, user],
  );
  const claimPermissions = user?.[permissionsKey] as string[] | undefined;

  useEffect(() => {
    if (!isAuthenticated || claimPermissions) {
      setPermissions(claimPermissions);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        // Deliberately the shared accessor rather than `getAccessTokenSilently`
        // directly: this hook runs at start-up alongside the GraphQL client, and
        // two independent token-acquisition paths meant two different error
        // policies for the same failure. The accessor owns the one policy —
        // including tearing down and restarting the session when the refresh
        // token is dead — and resolves to `undefined` instead of throwing.
        const accessToken = await getAccessToken();
        const payload = decodeJwtPayload<Record<string, unknown>>(accessToken);
        if (!cancelled) {
          setPermissions((payload?.[permissionsKey] as string[]) ?? []);
        }
      } catch (tokenError) {
        Logger.warn("Unable to read permissions from access token", {
          message: (tokenError as Error)?.message,
        });
        if (!cancelled) setPermissions([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [claimPermissions, isAuthenticated, permissionsKey]);

  // The reducer replaces `state.roles`/`state.permissions` wholesale, so
  // dispatching an equivalent-but-new array on every render would produce a new
  // store reference, re-render every subscriber, and loop. Dispatch only when
  // the session actually changes.
  const lastDispatched = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (isLoading) return;

    const signature = JSON.stringify({
      sub: user?.sub ?? null,
      isAuthenticated: isAuthenticated && !error,
      roles,
      permissions: permissions ?? [],
    });
    if (lastDispatched.current === signature) return;
    lastDispatched.current = signature;

    if (isAuthenticated && !error) {
      dispatch(
        login({
          isAuthenticated: true,
          user: (user ?? null) as User | null,
          roles,
          permissions: permissions ?? [],
          isLoading: false,
        }),
      );
      return;
    }

    Logger.info("useAuthListener: logging out", { error, isLoading });
    dispatch(
      logout({
        isAuthenticated: false,
        user: null,
        roles: [],
        permissions: [],
        isLoading: false,
      }),
    );
  }, [dispatch, error, isAuthenticated, isLoading, permissions, roles, user]);

  return { isLoading, error, user, roles, permissions };
};

export default useAuthListener;
